import { useRef, useState } from 'react';
import CheckoutAlert from '../CheckoutAlert';
import { PasswordRules, TextField, buttonClass } from '../CheckoutControls';
import { requestPasswordReset } from '@/lib/checkout/services';
import { evaluatePassword, validateLoginStep } from '@/lib/checkout/validation';
import type { FieldErrors, LoginDraft } from '@/lib/checkout/types';

interface LoginStepProps {
  value: LoginDraft;
  onChange: (patch: Partial<LoginDraft>) => void;
  onNext: () => void;
  onSignIn: () => void;
  /**
   * AR-420 — creates the account (`checkout_pending`) on the server. Resolves
   * `exists` when the email already belongs to an account; throws when the
   * server cannot be reached or refuses.
   */
  onCreateAccount: () => Promise<'started' | 'exists'>;
}

type ServiceState = 'idle' | 'checking' | 'exists' | 'error';

/** Step 1 — Create Your Login. */
export default function LoginStep({ value, onChange, onNext, onSignIn, onCreateAccount }: LoginStepProps) {
  const [submitted, setSubmitted] = useState(false);
  const [service, setService] = useState<ServiceState>('idle');
  const [forgotOpen, setForgotOpen] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetSending, setResetSending] = useState(false);
  const [resetFailed, setResetFailed] = useState(false);
  const summaryRef = useRef<HTMLDivElement>(null);

  // Errors are derived once they've tried to continue, so they clear as fields are fixed.
  const validationErrors: FieldErrors = submitted ? validateLoginStep(value) : {};
  const errors: FieldErrors = {
    ...validationErrors,
    ...(service === 'exists'
      ? { email: 'An account already exists for this email. Sign in to continue.' }
      : {}),
  };
  const passwordRules = evaluatePassword(value.password);
  const showRules = value.password.length > 0 || Boolean(errors.password);
  const errorList = Object.keys(validationErrors);

  const submit = async () => {
    setSubmitted(true);

    if (Object.keys(validateLoginStep(value)).length > 0) {
      requestAnimationFrame(() => summaryRef.current?.focus());
      return;
    }

    setService('checking');

    let result: 'started' | 'exists';
    try {
      result = await onCreateAccount();
    } catch {
      // A rejected request must not strand them on a disabled button.
      setService('error');
      return;
    }

    if (result === 'exists') {
      setService('exists');
      return;
    }

    setService('idle');
    onNext();
  };

  const sendReset = async () => {
    setResetSending(true);
    try {
      await requestPasswordReset(value.email);
      setResetSent(true);
    } catch {
      setResetFailed(true);
    } finally {
      setResetSending(false);
    }
  };

  return (
    <>
      {service === 'exists' ? (
        <CheckoutAlert
          tone="error"
          title="An account already exists for this email"
          actions={
            <>
              <button type="button" onClick={onSignIn} className={buttonClass('sm')}>
                Sign In
              </button>
              <button
                type="button"
                onClick={() => {
                  setForgotOpen(true);
                  setResetSent(false);
                  setResetFailed(false);
                }}
                className={buttonClass('smOutline')}
              >
                Forgot password
              </button>
            </>
          }
        >
          Sign in to continue — we’ll resume your checkout exactly where you left off.
        </CheckoutAlert>
      ) : null}

      {service === 'error' ? (
        <CheckoutAlert
          tone="error"
          title="We couldn’t finish setting up your account"
          actions={
            <button type="button" onClick={submit} className={buttonClass('sm')}>
              Try again
            </button>
          }
        >
          Something went wrong on our side. Nothing has been charged and everything you entered has
          been saved — try again, or call (855) 846-7821 and we’ll finish it with you.
        </CheckoutAlert>
      ) : null}

      <h2 className="m-0 mb-1.5 text-xl font-bold text-[#4b0a5b] sm:text-[21px]">
        Create Your Login
      </h2>
      <p className="mb-5 text-[13px] leading-relaxed text-[#7a6a8c]">
        Create your login to get started. You will receive all account notifications at this email
        address.
      </p>

      {forgotOpen ? (
        resetSent ? (
          <CheckoutAlert
            tone="success"
            title="Check your email"
            actions={
              <button
                type="button"
                onClick={() => setForgotOpen(false)}
                className={buttonClass('smOutline')}
              >
                Back to checkout
              </button>
            }
          >
            We sent password reset instructions to <strong>{value.email}</strong>. Follow the link in
            that email, then come back here to finish checkout.
          </CheckoutAlert>
        ) : (
          <div className="mb-5 rounded-xl border border-[#e7ddef] bg-[#f6f2fb] p-4">
            <h3 className="m-0 mb-1 text-[13.5px] font-bold text-[#4b0a5b]">Reset your password</h3>
            <p className="mb-3 text-[12.5px] leading-relaxed text-[#5c4a6e]">
              We’ll email a reset link to the address below.
            </p>
            {resetFailed ? (
              <CheckoutAlert tone="error" title="We couldn’t send the reset email." compact>
                Try again in a moment, or call (855) 846-7821.
              </CheckoutAlert>
            ) : null}
            <TextField
              id="reset-email"
              label="Email"
              type="email"
              required
              value={value.email}
              onChange={(event) => onChange({ email: event.target.value })}
            />
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={sendReset}
                disabled={resetSending}
                className={buttonClass('sm')}
              >
                {resetSending ? 'Sending…' : 'Send reset link'}
              </button>
              <button
                type="button"
                onClick={() => setForgotOpen(false)}
                className={buttonClass('smOutline')}
              >
                Cancel
              </button>
            </div>
          </div>
        )
      ) : null}

      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <TextField
          id="login-email"
          label="Email"
          type="email"
          required
          autoComplete="email"
          hint="You will receive all account notifications at this email address"
          value={value.email}
          error={errors.email}
          onChange={(event) => {
            onChange({ email: event.target.value });
            if (service !== 'idle') setService('idle');
          }}
        />

        <div className="flex flex-col gap-0 sm:flex-row sm:gap-3">
          <TextField
            id="login-first-name"
            label="First Name"
            required
            className="sm:flex-1"
            autoComplete="given-name"
            value={value.firstName}
            error={errors.firstName}
            onChange={(event) => onChange({ firstName: event.target.value })}
          />
          <TextField
            id="login-last-name"
            label="Last Name"
            required
            className="sm:flex-1"
            autoComplete="family-name"
            value={value.lastName}
            error={errors.lastName}
            onChange={(event) => onChange({ lastName: event.target.value })}
          />
        </div>

        <div className="mb-4">
          <TextField
            id="login-password"
            label="Create Password"
            type="password"
            required
            autoComplete="new-password"
            hint="At least 11 characters, with an uppercase and lowercase letter, a number and a symbol"
            value={value.password}
            error={errors.password}
            onChange={(event) => onChange({ password: event.target.value })}
            className="mb-0"
          />
          {showRules ? <PasswordRules id="login-password-rules" rules={passwordRules} /> : null}
        </div>

        <TextField
          id="login-confirm-password"
          label="Confirm Password"
          type="password"
          required
          autoComplete="new-password"
          value={value.confirmPassword}
          error={errors.confirmPassword}
          onChange={(event) => onChange({ confirmPassword: event.target.value })}
        />

        {errorList.length > 0 ? (
          <div ref={summaryRef} tabIndex={-1}>
            <CheckoutAlert tone="error" title="Check the highlighted fields." compact>
              {errorList.length === 1
                ? '1 field needs attention before you can continue.'
                : `${errorList.length} fields need attention before you can continue.`}
            </CheckoutAlert>
          </div>
        ) : null}

        <CheckoutAlert tone="info" title="Already have an account?" compact live={false}>
          <button
            type="button"
            onClick={onSignIn}
            className="font-semibold text-[#b82f74] underline"
          >
            Sign in
          </button>{' '}
          — we’ll pick your checkout up where you left off.
        </CheckoutAlert>

        <div className="mt-4 flex gap-2">
          <button type="submit" disabled={service === 'checking'} className={buttonClass('primary')}>
            {service === 'checking' ? 'Checking…' : 'Next'}
          </button>
        </div>
      </form>
    </>
  );
}
